import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, X, Sparkles } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';

interface UsernameSelectionModalProps {
  isOpen: boolean;
  currentUsername: string;
  onUsernameSet: (username: string) => void;
  onClose?: () => void;
  requireUsername?: boolean; // If true, user must set username before proceeding
}

export function UsernameSelectionModal({
  isOpen,
  currentUsername,
  onUsernameSet,
  onClose,
  requireUsername = false
}: UsernameSelectionModalProps) {
  const [username, setUsername] = useState(currentUsername);
  const [originalUsername, setOriginalUsername] = useState(currentUsername);
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      setUsername(currentUsername);
      setOriginalUsername(currentUsername);
      setError('');
    }
  }, [isOpen, currentUsername]);

  // Real-time username validation with debouncing
  useEffect(() => {
    // Skip validation if modal is closed, username is too short, or unchanged
    if (!isOpen || !username || username.length < 3 || username === originalUsername) {
      return;
    }

    // Debounce: wait 500ms after user stops typing
    const timeoutId = setTimeout(async () => {
      await checkUsernameAvailability(username);
    }, 500);

    // Cleanup: cancel timeout if username changes again before 500ms
    return () => clearTimeout(timeoutId);
  }, [username, isOpen, originalUsername]);

  const handleUsernameChange = (value: string) => {
    const cleanedUsername = value.toLowerCase().replace(/[^a-z0-9]/g, '');
    setUsername(cleanedUsername);
    setError('');
  };

  const checkUsernameAvailability = async (usernameToCheck: string): Promise<boolean> => {
    if (!usernameToCheck || usernameToCheck.length < 3) {
      setError(usernameToCheck.length > 0 ? 'Username must be at least 3 characters' : '');
      return false;
    }

    setIsChecking(true);
    setError('');

    try {
      const response = await fetch('/api/check-username', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: usernameToCheck })
      });

      const data = await response.json();

      if (!data.available) {
        setError(data.error || 'Username is already taken');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking username:', error);
      setError('Error checking availability');
      return false;
    } finally {
      setIsChecking(false);
    }
  };

  const handleGenerateUsername = async () => {
    setIsGenerating(true);
    setError('');
    
    try {
      const response = await fetch('/api/generate-username');
      const data = await response.json();
      
      if (response.ok && data.username) {
        setUsername(data.username);
        toast({
          title: 'Username Generated!',
          description: `Try "${data.username}" or customize it further.`,
        });
      } else {
        throw new Error(data.error || 'Failed to generate username');
      }
    } catch (error: any) {
      console.error('Error generating username:', error);
      setError(error.message || 'Unable to generate username. Please try again.');
      toast({
        title: 'Error',
        description: 'Unable to generate username. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!username || username.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }

    // Prevent saving if username hasn't been changed from the original auto-generated one
    if (username === originalUsername) {
      setError('Please customize your username before saving');
      return;
    }

    // Check availability since username was changed
    const isAvailable = await checkUsernameAvailability(username);
    if (!isAvailable) {
      return;
    }

    setIsSaving(true);
    try {
      await apiRequest('PUT', '/api/user/profile', {
        body: { 
          username,
          hasCustomizedUsername: true // Mark that user has customized their username
        }
      });

      toast({
        title: 'Username Set!',
        description: `Your username "${username}" has been saved successfully.`,
      });

      onUsernameSet(username);
    } catch (error: any) {
      console.error('Error saving username:', error);
      setError(error.message || 'Failed to save username. Please try again.');
      toast({
        title: 'Error',
        description: 'Failed to save username. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-[600px] bg-white border-gray-300 text-black [&>button]:hidden"
      >
        {onClose && !requireUsername && (
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full p-2 bg-gray-200 hover:bg-gray-300 transition-colors z-50"
            data-testid="button-close-username-modal"
          >
            <X className="h-5 w-5 text-black" />
          </button>
        )}
        
        <div className="flex flex-col items-center py-8 px-6">
          <h2 className="text-3xl font-bold mb-2" data-testid="text-modal-title">Set Username</h2>
          
          {/* Trophy illustration */}
          <div className="my-8 relative">
            <div className="flex items-end justify-center gap-4">
              {/* Left podium */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-yellow-400 flex items-center justify-center mb-2 animate-bounce" style={{ animationDelay: '0.1s' }}>
                  <span className="text-2xl">💰</span>
                </div>
                <div className="w-20 h-24 bg-gradient-to-t from-green-400 to-green-300 rounded-t-lg border-2 border-green-500"></div>
              </div>
              
              {/* Center podium (tallest) */}
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 mb-2">
                  <div className="text-6xl animate-bounce">🏆</div>
                </div>
                <div className="w-24 h-32 bg-gradient-to-t from-green-400 to-green-300 rounded-t-lg border-2 border-green-500"></div>
              </div>
              
              {/* Right podium */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-yellow-400 flex items-center justify-center mb-2 animate-bounce" style={{ animationDelay: '0.2s' }}>
                  <span className="text-2xl">💰</span>
                </div>
                <div className="w-20 h-20 bg-gradient-to-t from-green-400 to-green-300 rounded-t-lg border-2 border-green-500"></div>
              </div>
            </div>
          </div>

          <h3 className="text-2xl font-semibold text-center mb-3 text-black" data-testid="text-modal-subtitle">
            {requireUsername ? 'Set Your Username to Continue' : 'Set Your Username to Join the Competition'}
          </h3>
          <p className="text-gray-600 text-center mb-8" data-testid="text-modal-description">
            {requireUsername 
              ? 'Choose a username to appear on the challenge leaderboard' 
              : 'Usernames are required to appear on the leaderboard'
            }
          </p>

          <div className="w-full max-w-md">
            <Input
              type="text"
              placeholder="Enter Username"
              value={username}
              onChange={(e) => handleUsernameChange(e.target.value)}
              className="w-full bg-white border-gray-300 text-black placeholder:text-gray-400 text-lg py-6 px-4 focus:border-blue-500 focus:ring-blue-500"
              disabled={isSaving || isChecking || isGenerating}
              data-testid="input-username"
            />
            
            <Button
              onClick={handleGenerateUsername}
              disabled={isSaving || isChecking || isGenerating}
              variant="outline"
              className="w-full mt-3 border-2 border-purple-400 text-purple-600 hover:bg-purple-50 hover:border-purple-500 font-medium py-5"
              data-testid="button-generate-username"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Random Username
                </>
              )}
            </Button>
            
            {isChecking && (
              <p className="text-sm text-blue-600 mt-2 flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" />
                Checking availability...
              </p>
            )}
            
            {error && (
              <p className="text-sm text-red-600 mt-2" data-testid="text-username-error">
                {error}
              </p>
            )}
            
            {!error && username && username.length >= 3 && !isChecking && username === originalUsername && (
              <p className="text-sm text-orange-600 mt-2 font-medium" data-testid="text-username-unchanged">
                ⚠️ Please customize your username
              </p>
            )}
            
            {!error && username && username.length >= 3 && !isChecking && username !== originalUsername && (
              <p className="text-sm text-green-600 mt-2" data-testid="text-username-available">
                ✓ Username available
              </p>
            )}
            
            <p className="text-xs text-gray-500 mt-2">
              Lowercase letters and numbers only (minimum 3 characters)
            </p>
          </div>

          <Button
            onClick={handleSave}
            disabled={isSaving || isChecking || !username || username.length < 3 || !!error}
            className="mt-8 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-semibold px-12 py-6 text-lg rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="button-save-username"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
